// Simple cart drawer implementation

// Base cart component class
class CartComponent extends HTMLElement {
  refs = {};

  connectedCallback() {
    this.setupRefs();
    this.setupEventHandlers();
  }

  setupRefs() {
    // Reset and rebuild refs each time we (re)scan DOM
    this.refs = {};
    const refElements = this.querySelectorAll('[ref]');

    refElements.forEach((element) => {
      const refName = element.getAttribute('ref');

      if (refName.endsWith('[]')) {
        const arrayName = refName.slice(0, -2);
        if (!this.refs[arrayName]) {
          this.refs[arrayName] = [];
        }
        this.refs[arrayName].push(element);
      } else {
        this.refs[refName] = element;
      }
    });
  }

  setupEventHandlers() {
    // Handle on:click attributes
    const clickElements = this.querySelectorAll('[on\\:click]');

    clickElements.forEach((element) => {
      const handlerPath = element.getAttribute('on:click');
      element.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleEvent(event, handlerPath);
      });
    });

    // Special handling for close buttons
    const closeButtons = this.querySelectorAll('[ref="closeButton"]');
    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof this.close === 'function') {
          this.close();
        } else if (typeof this.closeDialog === 'function') {
          this.closeDialog();
        }
      });
    });
  }

  handleEvent(event, handlerPath) {
    if (handlerPath.startsWith('/')) {
      const parts = handlerPath.slice(1).split('/');
      const methodName = parts[0];
      const args = parts.slice(1);

      if (typeof this[methodName] === 'function') {
        this[methodName](...args);
      }
    }
  }
}

// Dialog functionality that properly handles scroll-lock
class DialogComponent extends CartComponent {
  #previousActiveElement;
  connectedCallback() {
    super.connectedCallback();
    this.#setupFocusTrap();
  }

  showDialog() {
    const dialog = this.refs.dialog;

    if (!dialog) {
      console.error('Dialog ref not found');
      return;
    }

    if (dialog.open) return;

    dialog.showModal();

    // Wait for next frame, then trigger slide-in
    requestAnimationFrame(() => {
      dialog.classList.add('cart-drawer--visible');
    });
    // Remember previously focused element to restore later
    this.#previousActiveElement = document.activeElement;
    // Move focus to close button or dialog
    const firstFocusable = this.#getFocusableElements()[0] || dialog;
    firstFocusable?.focus();

    // Add backdrop click handler
    setTimeout(() => {
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) {
          this.closeDialog();
        }
      });
    });
  }

  closeDialog() {
    const dialog = this.refs.dialog;

    if (!dialog || !dialog.open) return;

    // Remove visible class to trigger slide-out
    dialog.classList.remove('cart-drawer--visible');

    // Wait for animation to complete before closing
    setTimeout(() => {
      dialog.close();
      // Restore focus to the previous trigger
      this.#previousActiveElement?.focus?.();
    }, 300);
  }

  #setupFocusTrap() {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    dialog.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusables = this.#getFocusableElements();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Close with Escape for completeness (dialog handles by default but ensure)
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDialog();
      }
    });
  }

  #getFocusableElements() {
    const dialog = this.refs.dialog;
    if (!dialog) return [];
    const selectors = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex="-1"])',
    ];
    return Array.from(dialog.querySelectorAll(selectors.join(','))).filter(
      (el) => el.offsetParent !== null || dialog === el
    );
  }
}

// Cart drawer component
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    // Set up global cart drawer access for single drawer structure
    const allCartDrawers = document.querySelectorAll('cart-drawer-component');
    // Always expose the first/only instance for global triggers
    window.cartDrawer = this;

    // Listen for cart updates to refresh drawer content
    document.addEventListener('cartUpdate', this.handleCartUpdate.bind(this));

    // Set up event delegation for recommendation buttons (since they get updated via AJAX)
    this.addEventListener('click', (e) => {
      const addButton = e.target.closest('.cart-recommendations__add-button');
      if (addButton) {
        e.preventDefault();
        const variantId = addButton.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId);
        }
      }
    });

    // Auto-open for styling purposes if attribute is present
    if (this.hasAttribute('auto-open-for-styling')) {
      // Wait for DOM to be ready, then open the drawer
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(() => this.open(), 100);
        });
      } else {
        setTimeout(() => this.open(), 100);
      }
    }
  }

  async open() {
    try {
      // Show dialog first so elements exist in DOM
      this.showDialog();

      // Wait for dialog to fully render before refreshing content
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Then refresh cart content
      await this.refreshDrawerContent();
    } catch (error) {
      console.error('ERROR in CartDrawerComponent.open():', error);
      console.error('Error stack:', error.stack);
    }
  }

  close() {
    this.closeDialog();
  }

  handleCartUpdate(event) {
    // If drawer is open, refresh its content
    if (this.refs.dialog && this.refs.dialog.open) {
      this.refreshDrawerContent();
    }
  }

  async addToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw await response
          .json()
          .catch(() => ({ message: 'Add to cart failed' }));
      }

      const addResult = await response.json();

      // Dispatch cart update event for bubble update
      document.dispatchEvent(
        new CustomEvent('cartUpdate', {
          detail: {
            data: {
              itemCount: 1,
              source: 'cart-recommendations',
            },
          },
        })
      );

      // Refresh the drawer content to show updated recommendations
      await this.refreshDrawerContent();
    } catch (error) {
      console.error('Error adding recommendation to cart:', error);
    }
  }

  async refreshDrawerContent() {
    try {
      // Fetch the dedicated cart content section for consistent markup and styles
      const url = `${window.location.pathname}?section_id=cart-content`;
      const response = await fetch(url);
      const html = await response.text();

      // Parse the response and update content carefully to preserve structure
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newCartProducts = doc.querySelector('[data-cart-products]');
      const newCartSummary = doc.querySelector('[data-cart-summary]');
      const newCartRecommendations = doc.querySelector(
        '[data-cart-recommendations]'
      );
      const newFreeShippingBar = doc.querySelector('[data-free-shipping-bar]');

      const currentCartProducts = this.querySelector(
        'cart-items-component [data-cart-products]'
      );
      const currentCartSummary = this.querySelector(
        'cart-items-component [data-cart-summary]'
      );
      const currentCartRecommendations = this.querySelector(
        'cart-items-component [data-cart-recommendations]'
      );
      const currentFreeShippingBar = this.querySelector(
        '[data-free-shipping-bar]'
      );

      if (newCartProducts && currentCartProducts) {
        currentCartProducts.innerHTML = newCartProducts.innerHTML;
      }

      if (newCartSummary && currentCartSummary) {
        currentCartSummary.innerHTML = newCartSummary.innerHTML;
      }

      if (newCartRecommendations && currentCartRecommendations) {
        currentCartRecommendations.innerHTML = newCartRecommendations.innerHTML;
      }

      if (newFreeShippingBar && currentFreeShippingBar) {
        currentFreeShippingBar.innerHTML = newFreeShippingBar.innerHTML;
      }

      // Toggle empty state styles and empty heading visibility based on updated content
      const dialog = this.refs.dialog;
      const hasRows = !!this.querySelector(
        'cart-items-component .cart-items__table-row'
      );
      if (dialog) {
        dialog.classList.toggle('cart-drawer--empty', !hasRows);
      }
      const emptyHeading = this.querySelector('.cart-drawer__heading--empty');
      if (emptyHeading) {
        emptyHeading.style.display = hasRows ? 'none' : '';
      }

      // Re-initialize refs and event handlers on cart-items-component after HTML replacement
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (cartItemsComponent) {
        if (typeof cartItemsComponent.setupRefs === 'function') {
          cartItemsComponent.setupRefs();
        }
        if (typeof cartItemsComponent.setupEventHandlers === 'function') {
          cartItemsComponent.setupEventHandlers();
        }
      }

      // Also re-initialize event handlers on the cart drawer itself for recommendation buttons
      this.setupRefs();
      this.setupEventHandlers();
    } catch (error) {
      console.error('Error refreshing cart drawer content:', error);
    }
  }
}

// Cart icon component
class CartIconComponent extends CartComponent {
  connectedCallback() {
    super.connectedCallback();
    // Listen for cart update events
    document.addEventListener('cartUpdate', this.onCartUpdate.bind(this));
  }

  onCartUpdate(event) {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm =
      event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  }

  renderCartBubble(itemCount, comingFromProductForm = false) {
    // Use refs like Horizon does
    const cartBubble = this.querySelector('[ref="cartBubble"]');
    const cartBubbleCount = this.querySelector('[ref="cartBubbleCount"]');

    if (cartBubble && cartBubbleCount) {
      // Update count
      if (comingFromProductForm) {
        const currentCount = parseInt(cartBubbleCount.textContent || '0', 10);
        const newCount = currentCount + itemCount;
        cartBubbleCount.textContent = newCount;
      } else {
        cartBubbleCount.textContent = itemCount;
      }

      // Show/hide bubble based on count
      const finalCount = parseInt(cartBubbleCount.textContent || '0', 10);
      if (finalCount === 0) {
        cartBubble.classList.add('visually-hidden');
        cartBubbleCount.classList.add('hidden');
      } else {
        cartBubble.classList.remove('visually-hidden');
        cartBubbleCount.classList.remove('hidden');
      }
    }
  }
}

// Text component for shimmer effects
class TextComponent extends HTMLElement {
  shimmer() {
    this.style.opacity = '0.5';
    setTimeout(() => {
      this.style.opacity = '';
    }, 300);
  }
}

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Update specific cart areas without replacing the entire section
const updateCartContent = (sections) => {
  if (!sections) return;

  Object.keys(sections).forEach((sectionId) => {
    const newHTML = sections[sectionId];
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(newHTML, 'text/html');

    const drawerComponent = document.querySelector('cart-drawer-component');
    const drawerRoot = drawerComponent ?? document;

    // Update cart products
    const newCartProducts = newDoc.querySelector('[data-cart-products]');
    const currentCartProducts = drawerRoot.querySelector(
      '[data-cart-products]'
    );
    if (newCartProducts && currentCartProducts) {
      currentCartProducts.innerHTML = newCartProducts.innerHTML;
    }

    // Update cart summary
    const newCartSummary = newDoc.querySelector('[data-cart-summary]');
    const currentCartSummary = drawerRoot.querySelector('[data-cart-summary]');
    if (newCartSummary && currentCartSummary) {
      currentCartSummary.innerHTML = newCartSummary.innerHTML;
    }

    // Update cart recommendations
    const newCartRecommendations = newDoc.querySelector(
      '[data-cart-recommendations]'
    );
    const currentCartRecommendations = drawerRoot.querySelector(
      '[data-cart-recommendations]'
    );
    if (newCartRecommendations && currentCartRecommendations) {
      currentCartRecommendations.innerHTML = newCartRecommendations.innerHTML;
    }

    // Update free shipping bar
    const newFreeShippingBar = newDoc.querySelector('[data-free-shipping-bar]');
    const currentFreeShippingBar = drawerRoot.querySelector(
      '[data-free-shipping-bar]'
    );
    if (newFreeShippingBar && currentFreeShippingBar) {
      currentFreeShippingBar.innerHTML = newFreeShippingBar.innerHTML;
    }

    // Toggle empty state styles and empty heading visibility based on updated content
    const dialog = drawerRoot?.querySelector('[ref="dialog"]');
    const hasRows = !!drawerRoot?.querySelector(
      'cart-items-component .cart-items__table-row'
    );
    if (dialog) {
      dialog.classList.toggle('cart-drawer--empty', !hasRows);
    }
    const emptyHeading = drawerRoot?.querySelector(
      '.cart-drawer__heading--empty'
    );
    if (emptyHeading) {
      emptyHeading.style.display = hasRows ? 'none' : '';
    }

    // Update cart bubble in header
    const newCartBubble = newDoc.querySelector('.cart-bubble__text-count');
    const currentCartBubble = document.querySelector(
      '.cart-bubble__text-count'
    );
    if (newCartBubble && currentCartBubble) {
      currentCartBubble.textContent = newCartBubble.textContent;

      // Show/hide cart bubble based on count
      const cartBubbleContainer = document.querySelector('.cart-bubble');
      if (cartBubbleContainer) {
        if (parseInt(newCartBubble.textContent || '0', 10) === 0) {
          cartBubbleContainer.classList.add('visually-hidden');
          currentCartBubble.classList.add('hidden');
          // Close drawer if empty
          const drawerComponent = document.querySelector(
            'cart-drawer-component'
          );
          if (drawerComponent && typeof drawerComponent.close === 'function') {
            drawerComponent.close();
          }
        } else {
          cartBubbleContainer.classList.remove('visually-hidden');
          currentCartBubble.classList.remove('hidden');
        }
      }
    }

    // Re-initialize refs and event handlers after HTML replacement
    const cic = drawerRoot.querySelector('cart-items-component');
    if (cic) {
      if (typeof cic.setupRefs === 'function') cic.setupRefs();
      if (typeof cic.setupEventHandlers === 'function')
        cic.setupEventHandlers();
    }

    // Also re-initialize event handlers on cart drawer for recommendation buttons
    const cartDrawer = drawerComponent;
    if (cartDrawer) {
      if (typeof cartDrawer.setupRefs === 'function') cartDrawer.setupRefs();
      if (typeof cartDrawer.setupEventHandlers === 'function')
        cartDrawer.setupEventHandlers();
    }
  });
};

// Quantity selector component
class QuantitySelectorComponent extends CartComponent {
  #debouncedOnChange = debounce(this.#onQuantityChange.bind(this), 300);

  connectedCallback() {
    super.connectedCallback();

    // Listen for quantity selector update events
    this.addEventListener('quantityChange', this.#debouncedOnChange);
  }

  decreaseQuantity() {
    const input = this.refs.quantityInput;
    if (!input) return;

    const currentValue = parseInt(input.value, 10);
    const step = parseInt(input.step, 10) || 1;
    const min = parseInt(input.min, 10) || 0;

    const newValue = Math.max(currentValue - step, min);
    input.value = newValue;

    this.dispatchQuantityUpdate(newValue);
  }

  increaseQuantity() {
    const input = this.refs.quantityInput;
    if (!input) return;

    const currentValue = parseInt(input.value, 10);
    const step = parseInt(input.step, 10) || 1;
    const max = input.max ? parseInt(input.max, 10) : Infinity;

    const newValue = Math.min(currentValue + step, max);
    input.value = newValue;

    this.dispatchQuantityUpdate(newValue);
  }

  setQuantity() {
    const input = this.refs.quantityInput;
    if (!input) return;

    const value = parseInt(input.value, 10);
    if (isNaN(value)) {
      input.value = input.defaultValue || 1;
      return;
    }

    const min = parseInt(input.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;

    const clampedValue = Math.max(min, Math.min(value, max));
    input.value = clampedValue;

    this.dispatchQuantityUpdate(clampedValue);
  }

  selectInputValue() {
    const input = this.refs.quantityInput;
    if (!input) return;

    input.select();
  }

  dispatchQuantityUpdate(quantity) {
    const input = this.refs.quantityInput;
    const cartLine = input?.dataset.cartLine
      ? parseInt(input.dataset.cartLine, 10)
      : null;

    // Always dispatch quantity-selector:update event for product forms
    document.dispatchEvent(
      new CustomEvent('quantity-selector:update', {
        detail: { quantity, cartLine },
        bubbles: true,
      })
    );

    if (cartLine) {
      // Dispatch custom event for debounced handling in cart
      this.dispatchEvent(
        new CustomEvent('quantityChange', {
          detail: { quantity, cartLine },
          bubbles: true,
        })
      );
    }
  }

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.removeLineItem(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    // Add shimmer effect to the line total while updating
    const cartItemsComponent = document.querySelector('cart-items-component');
    const lineItemRow = cartItemsComponent?.refs?.cartItemRows?.[line - 1];
    if (lineItemRow) {
      const textComponent = lineItemRow.querySelector('text-component');
      textComponent?.shimmer();
    }
  }

  removeLineItem(line) {
    // Delegate to CartItemsComponent
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && cartItemsComponent.onLineItemRemove) {
      cartItemsComponent.onLineItemRemove(line);
    }
  }

  updateQuantity(config) {
    // Delegate to CartItemsComponent
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && cartItemsComponent.updateQuantity) {
      cartItemsComponent.updateQuantity(config);
    }
  }
}

// Cart items component
class CartItemsComponent extends CartComponent {
  connectedCallback() {
    super.connectedCallback();

    // Listen for cart update events to refresh cart bubble
    document.addEventListener('cartUpdate', this.handleCartUpdate.bind(this));

    // Delegate clicks for remove buttons to avoid rebinding issues after DOM updates
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.cart-items__remove');
      if (!removeBtn) return;
      e.preventDefault();
      const row = removeBtn.closest('[ref="cartItemRows[]"]');
      const rows = this.refs?.cartItemRows || [];
      const line = rows.indexOf(row) + 1;
      if (line > 0) this.onLineItemRemove(line);
    });
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // Add animation for removal
    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];
    if (cartItemRowToRemove) {
      cartItemRowToRemove.style.setProperty(
        '--row-height',
        `${cartItemRowToRemove.clientHeight}px`
      );
      cartItemRowToRemove.classList.add('removing');

      // Remove the row after animation
      setTimeout(() => {
        cartItemRowToRemove.remove();
      }, 500);
    }
  }

  updateQuantity(config) {
    this.#disableCartItems();

    const { line, quantity } = config;

    // Get all cart sections that need updating
    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component'
    );
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    // Add shimmer to cart total
    const cartTotal = document.querySelector('text-component[data-cart-total]');
    cartTotal?.shimmer();

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          console.error(
            'CartItemsComponent cart update errors:',
            parsedResponseText.errors
          );
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // Update cart content areas without replacing the entire section
        updateCartContent(parsedResponseText.sections);

        // Dispatch cart update event for cart bubble
        const newCartItemCount = parsedResponseText.item_count || 0;
        document.dispatchEvent(
          new CustomEvent('cartUpdate', {
            detail: {
              data: {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
              },
            },
          })
        );
      })
      .catch((error) => {
        console.error('CartItemsComponent cart update error:', error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  handleCartUpdate(event) {
    // Handle updates from other sources if needed
  }

  #handleCartError(line, parsedResponseText) {
    const quantitySelector = this.refs?.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs?.[`cartItemError-${line}`];
    const cartItemErrorContainer =
      this.refs?.[`cartItemErrorContainer-${line}`];

    if (cartItemError && cartItemErrorContainer) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }
  }

  #disableCartItems() {
    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component'
    );
    cartItemsComponents.forEach((component) => {
      component.classList.add('cart-items-disabled');
    });
  }

  #enableCartItems() {
    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component'
    );
    cartItemsComponents.forEach((component) => {
      component.classList.remove('cart-items-disabled');
    });
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

// Register all custom elements
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIconComponent);
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

if (!customElements.get('text-component')) {
  customElements.define('text-component', TextComponent);
}

// Use real viewport height for mobile devices (to avoid issues with toolbars covering content)
const setRealViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--real-vh', `${vh}px`);
};

// Set initial viewport height
setRealViewportHeight();

// Update on window resize (important for mobile when browser chrome shows/hides)
window.addEventListener('resize', setRealViewportHeight);

// Also update on orientation change (mobile devices)
window.addEventListener('orientationchange', () => {
  // Small delay to ensure the viewport has adjusted
  setTimeout(setRealViewportHeight, 100);
});

// Handle scroll-lock attribute on dialogs
document.addEventListener(
  'toggle',
  (event) => {
    if (
      event.target instanceof HTMLDialogElement ||
      event.target instanceof HTMLDetailsElement
    ) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

// Add backup click handlers
document.addEventListener('DOMContentLoaded', () => {
  const cartTriggers = document.querySelectorAll('.cart-drawer__trigger');
  cartTriggers.forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const cartDrawer =
        trigger.closest('cart-drawer-component') || window.cartDrawer;
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    });
  });

  // Ensure global reference exists even if component connected earlier/later
  if (!window.cartDrawer) {
    const firstDrawer = document.querySelector('cart-drawer-component');
    if (firstDrawer) window.cartDrawer = firstDrawer;
  }
});

// AJAX add-to-cart: intercept product form submit, add via /cart/add.js, re-render header section, open drawer
(() => {
  const getDrawer = () => document.querySelector('cart-drawer-component');
  const getDrawerOpenFn = () => getDrawer()?.open?.bind(getDrawer());

  const getHeaderSectionId = () => {
    const comp = document.querySelector('cart-items-component');
    return comp?.dataset.sectionId || null;
  };

  const headerWrapperSelector = (sectionId) => `#shopify-section-${sectionId}`;

  const replaceHeaderSection = async () => {
    const sectionId = getHeaderSectionId();
    if (!sectionId) return false;

    const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
    const html = await fetch(url, { credentials: 'same-origin' }).then((r) =>
      r.text()
    );

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newWrapper = doc.querySelector(headerWrapperSelector(sectionId));
    const currentWrapper = document.querySelector(
      headerWrapperSelector(sectionId)
    );

    if (newWrapper && currentWrapper) {
      currentWrapper.outerHTML = newWrapper.outerHTML;
      return true;
    }
    return false;
  };

  const handleAjaxAddToCart = async (form) => {
    const formData = new FormData(form);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
        credentials: 'same-origin',
      });

      if (!res.ok) {
        throw await res.json().catch(() => ({ message: 'Add to cart failed' }));
      }

      const addResult = await res.json();

      // Get quantity added from form data
      const quantityAdded = parseInt(formData.get('quantity') || '1', 10);

      // Dispatch cart update event for bubble update
      document.dispatchEvent(
        new CustomEvent('cartUpdate', {
          detail: {
            data: {
              itemCount: quantityAdded,
              source: 'product-form-component',
            },
          },
        })
      );

      // Open the cart drawer (it will auto-refresh its content)
      if (window.cartDrawer && window.cartDrawer.open) {
        window.cartDrawer.open();
      } else {
        // Fallback: Find cart drawer in DOM
        const drawerComponent = document.querySelector('cart-drawer-component');
        if (drawerComponent && typeof drawerComponent.open === 'function') {
          drawerComponent.open();
        } else {
          console.warn('Cart drawer component not found');
        }
      }
    } catch (e) {
      console.error('AJAX add to cart error', e);

      // Fallback: open drawer anyway
      if (window.cartDrawer && window.cartDrawer.open) {
        window.cartDrawer.open();
      } else {
        const drawerComponent = document.querySelector('cart-drawer-component');
        if (drawerComponent && typeof drawerComponent.open === 'function') {
          drawerComponent.open();
        }
      }
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form =
        event.target instanceof HTMLFormElement
          ? event.target
          : event.target.closest('form');
      if (!form) return;

      const isAddToCart =
        /\/cart\/(add|add\.js)/.test(form.action || '') ||
        form.querySelector('input[name="id"]') ||
        form.querySelector('select[name="id"]');

      if (!isAddToCart) return;

      // opt-out flag
      if (form.hasAttribute('data-no-ajax')) return;

      event.preventDefault();
      handleAjaxAddToCart(form);
    },
    true
  );
})();
